import { useEffect, useRef, useState } from "react";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import { Col, Form } from "react-bootstrap";

type Point = { x: number; y: number };

const CreateAIWFromRBR = () => {
  const { access } = useIsAdmin();
  const [output, setOutput] = useState<string>("");
  const [points, setPoints] = useState<Point[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [type, setType] = useState(3);

  useEffect(() => {
    document.title = "LVF1 - Create AIW From RBR";

    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Limpiar canvas
    ctx.clearRect(0, 0, width, height);

    // Calcular bounding box
    let xs = points.map((p) => p.x);
    xs = xs.filter(
      (n) => typeof n === "number" && !isNaN(n), //&& n >= -4000 && n <= 4000
    );
    let ys = points.map((p) => p.y);
    ys = ys.filter(
      (n) => typeof n === "number" && !isNaN(n), //&& n >= -4000 && n <= 4000
    );
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const padding = 20;
    const scaleX = (width - 2 * padding) / (maxX - minX || 1);
    const scaleY = (height - 2 * padding) / (maxY - minY || 1);

    // Dibujar línea conectando puntos
    ctx.strokeStyle = "blue";
    ctx.lineWidth = 1;
    ctx.beginPath();
    points.forEach((p, i) => {
      const x = padding + (p.x - minX) * scaleX;
      const y = height - (padding + (p.y - minY) * scaleY); // invertir Y para canvas
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Dibujar puntos
    ctx.fillStyle = "red";
    points.forEach((p) => {
      const x = padding + (p.x - minX) * scaleX;
      const y = height - (padding + (p.y - minY) * scaleY);
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, 2 * Math.PI);
      ctx.fill();
    });
  }, [points]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();

    const newPoints: Point[] = [];
    if (type === 3) {
      let result = "[DRIVELINE]\ncount=9999999";
      reader.onload = () => {
        const text = reader.result as string;

        // Separar líneas y quitar líneas vacías
        const lines = text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l.length > 0);

        // Quitamos la cabecera (fila 0)
        const dataLines = lines.slice(1);

        result = dataLines
          .map((line, index) => {
            const cleanLine = line.replace(/[\r\n]+/g, "");

            // Separar valores por coma
            const values = cleanLine.split(",");

            // Coger los dos primeros valores
            const x = Number(values[0]);
            const y = Number(values[1]);

            // Push al array
            newPoints.push({ x, y });

            return `K${index}=0, 0, 0, 0, 0, ${cleanLine}`;
          })
          .join("\n");
        setPoints(newPoints);
        setOutput(result);
      };

      reader.readAsText(file);
    } else {
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const dataView = new DataView(arrayBuffer);

        const blockSize = 8;
        const floatSize = 4;
        const totalBlocks = Math.floor(
          dataView.byteLength / (blockSize * floatSize),
        );
        let outputText = `[DRIVELINE]\ncount=${totalBlocks}\n`;

        for (let i = 0; i < totalBlocks; i++) {
          if (i > 366) continue;
          const values: number[] = [];
          for (let j = 0; j < blockSize; j++) {
            const value = dataView.getFloat32(
              (i * blockSize + j) * floatSize,
              true,
            );
            values.push(value);
          }
          outputText += `K${i}=${values.map((v) => v.toFixed(6)).join(", ")}\n`;
          newPoints.push({
            x: values[type == 1 ? 5 : 0],
            y: values[type == 1 ? 6 : 1],
          });
        }

        setPoints(newPoints);
        setOutput(outputText);
      };
      reader.readAsArrayBuffer(file);
    }
  };
  useEffect(() => {
    if (!output) return;

    const newPoints: Point[] = [];

    const lines = output.split("\n");
    for (const line of lines) {
      if (line.startsWith("K")) {
        const parts = line.split("=");
        if (parts.length !== 2) continue;

        const values = parts[1].split(",").map((v) => parseFloat(v));
        if (values.length >= 7) {
          const x = values[type === 1 ? 5 : 0];
          const y = values[type === 1 ? 6 : 1];
          if (!isNaN(x) && !isNaN(y)) {
            newPoints.push({ x, y });
          }
        }
      }
    }

    setPoints(newPoints);
  }, [type]);
  const handleDownload = () => {
    const blob = new Blob([output], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName.replace(/\.trk$/i, ".txt");
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadJSON = () => {
    const total = points.length;
    var coords = points.map((coord, i) => {
      const ratio = i / total;
      let sector = 3;
      if (ratio < 1 / 3) sector = 1;
      else if (ratio < 2 / 3) sector = 2;

      return {
        coords: [
          parseFloat(coord.x.toFixed(4)),
          parseFloat(coord.y.toFixed(4)),
        ],
        sector,
        orden: i + 1,
      };
    });

    const jsonString = JSON.stringify(coords);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    a.click();

    URL.revokeObjectURL(url);
  };

  if (!access) return <ErrorPage />;

  return (
    <div className="full-height padding-lg app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Create AIW From RBR" />
        <Form.Group className="form-group" as={Col} md="12">
          <Form.Label>Parse Type</Form.Label>
          <Form.Select
            value={type}
            name="circuit"
            required
            onChange={(e) => setType(parseInt(e.target.value))}
          >
            <option value="3">CSV exported from RBR Roadbook</option>
            <option value="1">Binary TRK files</option>
            <option value="2">RBR Tracks (driveline.ini)</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="12">
          <Form.Label>
            {type == 3
              ? `Select .csv file exported from RBR Roadbook`
              : `trk File (...\\RBR\\Maps\\TrackName\\)`}
          </Form.Label>
          <Form.Control
            type="file"
            onChange={handleFileChange}
            accept=".trk,.csv"
            className="form-control"
          />
        </Form.Group>

        {output && (
          <>
            <canvas
              ref={canvasRef}
              width={600}
              height={400}
              className="border border-red-500 bg-white canvas-rbr"
            />
          </>
        )}
        <b>Decoded Content</b>
        <br></br>
        <small>
          If track is BTB format, then you can paste it from
          ...\RBR\RX_CONTENT\Tracks\TRACK_NAME\driveline.ini
        </small>
        <textarea
          value={output}
          onChange={(e) => {
            const newOutput = e.target.value;
            setOutput(newOutput);

            const newPoints: Point[] = [];

            const lines = newOutput.split("\n");
            for (const line of lines) {
              if (line.startsWith("K")) {
                const parts = line.split("=");
                if (parts.length !== 2) continue;

                const values = parts[1].split(",").map((v) => parseFloat(v));
                if (values.length >= 7) {
                  const x = values[type == 1 || type == 3 ? 5 : 0];
                  const y = values[type == 1 || type == 3 ? 6 : 1];
                  if (!isNaN(x) && !isNaN(y)) {
                    newPoints.push({ x, y });
                  }
                }
              }
            }

            setPoints(newPoints);
          }}
          className="textarea-rbr form-control"
        ></textarea>
        <Form.Group className="form-group text-center" as={Col} md="12">
          <button
            onClick={handleDownloadJSON}
            style={{ marginTop: "1rem" }}
            className="btn btn-secondary"
          >
            📥 Download AIW
          </button>
        </Form.Group>
      </div>
    </div>
  );
};

export default CreateAIWFromRBR;
